import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAdminUserProfile } from '../Services/AdminUsers/AdminUsersApis';

const UserContext = createContext();

export const UserProvider = ({ children, isAuthenticated }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchUserProfile = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAdminUserProfile();
            if (response && response.success) {
                setUser(response.data);
                // Also store roleId in localStorage as it might be needed for other API calls 
                // but the user specifically asked to avoid local storage for "showing profile"
                const roleId = response.data.roleId || response.data.role_id || response.data.role?.id;
                if (roleId) {
                    localStorage.setItem('roleId', roleId);
                }
            } else {
                setError('Failed to fetch user profile');
            }
        } catch (err) {
            console.error("UserContext: Error fetching profile", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const isAuth = isAuthenticated !== undefined ? isAuthenticated : (localStorage.getItem('isAuthenticated') === 'true');
        if (isAuth) {
            fetchUserProfile();
        } else {
            setUser(null);
            setLoading(false);
        }
    }, [fetchUserProfile, isAuthenticated]);

    const logout = () => {
        setUser(null);
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('roleId');
        localStorage.removeItem('user'); // Clean up old storage if any
    };

    return (
        <UserContext.Provider value={{ user, setUser, loading, error, fetchUserProfile, logout }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
